// Area create — space_type SelectField is fed by useSpaceTypes() so
// the dropdown stays in sync with the area_space_type catalog
// (004_layout.sql seed; new entries land via migration, not runtime).

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { areas } from "../../api/endpoints/areas";
import { qk } from "../../api/queryKeys";
import { useSpaceTypes } from "../../hooks/useSpaceTypes";
import { FormShell } from "../../components/forms/FormShell";
import { SelectField } from "../../components/forms/SelectField";
import { TextField } from "../../components/forms/TextField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { slugify } from "../../lib/slug";
import type { AreaCreate } from "../../api/types/area";

interface AreaCreateFormProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
  floorSlug: string;
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
const EMPTY = { slug: "", label: "", space_type: "" };

export function AreaCreateForm({
  accountSlug,
  tenantSlug,
  siteSlug,
  floorSlug,
  onDone,
  onPendingChange,
}: AreaCreateFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState(EMPTY);
  const slugTouched = useRef(false); // see lib/slug.ts + AccountCreateForm.

  const catalog = useSpaceTypes();
  const catalogFailed = catalog.error !== null && catalog.error !== undefined;

  const options = useMemo(
    () =>
      (catalog.data ?? []).map((s) => ({
        value: s.slug,
        label: s.label,
      })),
    [catalog.data],
  );

  const slugError = useMemo(() => {
    if (!values.slug) return null;
    return SLUG_RE.test(values.slug) ? null : "Invalid slug.";
  }, [values.slug]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: AreaCreate = {
        slug: values.slug,
        label: values.label,
        space_type: values.space_type,
      };
      return areas.create(accountSlug, tenantSlug, siteSlug, floorSlug, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.areas(accountSlug, tenantSlug, siteSlug, floorSlug),
      });
      setValues(EMPTY);
      slugTouched.current = false;
      onDone();
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const isValid =
    values.slug !== "" &&
    SLUG_RE.test(values.slug) &&
    values.label !== "" &&
    values.space_type !== "" &&
    !catalogFailed;

  return (
    <FormShell
      submitLabel="Create area"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create area failed"
    >
      {catalogFailed && (
        <ErrorBanner
          error={catalog.error}
          title="Couldn't load the space_type catalog"
        />
      )}
      <TextField
        label="Label"
        value={values.label}
        onChange={(label) =>
          setValues((v) => ({
            ...v,
            label,
            slug: slugTouched.current ? v.slug : slugify(label),
          }))
        }
        required
        placeholder="Kitchen"
        helper="The human-readable label. Slug below is auto-derived."
      />
      <TextField
        label="Slug"
        value={values.slug}
        onChange={(slug) => {
          slugTouched.current = true;
          setValues((v) => ({ ...v, slug }));
        }}
        required
        placeholder="kitchen"
        helper="Auto-derived from label until you edit it. There is no rename — typos stick."
        error={slugError}
      />
      <SelectField
        label="Space type"
        value={values.space_type}
        onChange={(space_type) => setValues((v) => ({ ...v, space_type }))}
        required
        options={options}
        placeholder={
          catalogFailed
            ? "Catalog unavailable — see error above"
            : catalog.isPending
              ? "Loading…"
              : "Pick a space type"
        }
        disabled={catalogFailed || catalog.isPending}
        helper="Catalog values come from area_space_type; backend rejects unknown slugs with 422."
      />
    </FormShell>
  );
}
